// Words sent to clients with an invoice: a port of share_texts.dart, with
// the same messages from app_en.arb.
import { bankIsEmpty, type Business } from '@/core/business';
import { daysBetween, formatDate, type IsoDate } from '@/core/dates';
import { daysLate, type Invoice, totalsOf } from '@/core/invoice';
import { t } from '@/l10n/strings';

/** "Invoice INV-0143 from Tobi Studios". */
export const invoiceShareSubject = (invoice: Invoice, business: Business) => t('shareSubject', { number: invoice.number, business: business.name }).trim();

/** Who it's for, the amount, when it's due, where to pay and, with [link], where to see it. */
export function invoiceShareMessage(invoice: Invoice, business: Business, link?: string): string {
  const totals = totalsOf(invoice);
  const amount = totals.paid.isPositive ? totals.balance : totals.total;
  return [
    t('shareIntro', { client: invoice.client.name.trim(), number: invoice.number, amount: amount.format(), dueDate: formatDate(invoice.dueDate) }),
    !bankIsEmpty(business.bank) && payTo(invoice, business),
    link && t('shareViewOnline', { link }),
    signOff(business),
  ]
    .filter(Boolean)
    .join('\n\n');
}

/** A polite nudge that says what's still owed and whether it's late. */
export function invoiceReminderMessage(invoice: Invoice, business: Business, today: IsoDate): string {
  const totals = totalsOf(invoice);
  const client = invoice.client.name.trim();
  const owed = totals.balance.format();
  const due = formatDate(invoice.dueDate);
  const intro =
    daysLate(invoice, today) > 0
      ? t('reminderIntroOverdue', { client, number: invoice.number, amount: owed, dueDate: due })
      : daysBetween(today, invoice.dueDate) === 0
        ? t('reminderIntroDueToday', { client, number: invoice.number, amount: owed })
        : t('reminderIntroDue', { client, number: invoice.number, amount: owed, dueDate: due });
  return [intro, totals.paid.isPositive && t('reminderThanksPaid', { amount: totals.paid.format() }), !bankIsEmpty(business.bank) && payTo(invoice, business), signOff(business)]
    .filter(Boolean)
    .join('\n\n');
}

export const invoiceReminderSubject = (invoice: Invoice, business: Business) => t('reminderSubject', { number: invoice.number, business: business.name }).trim();

function payTo(invoice: Invoice, business: Business) {
  const bank = business.bank;
  const details = [bank.bankName, bank.accountNumber, bank.accountName].filter((s) => s.trim()).join(' · ');
  return t('sharePayTo', { details, number: invoice.number });
}

const signOff = (business: Business) => (business.name.trim() ? t('shareSignOff', { business: business.name.trim() }) : t('shareSignOffPlain'));
